import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { createSocket, ISocket } from '@/lib/socketAdapter';
import { API_CONFIG } from '@/lib/api';

const chatLog = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.log(...args);
};

function getOtherUserId(roomId: string, myId: string): string | null {
  if (!roomId?.startsWith('dm_')) return null;
  const parts = roomId.split('_');
  if (parts.length !== 3) return null;
  return parts[1] === myId ? parts[2] : parts[1];
}

interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  content: string;
  message_type: 'text' | 'image' | 'file' | 'system';
  metadata?: any;
  status?: 'sent' | 'delivered' | 'read';
  created_at: string;
  updated_at?: string;
  sender: {
    id: string;
    email: string;
    full_name: string;
    avatar_url?: string;
  } | null;
}

interface TeamMember {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: string;
  is_online: boolean;
  last_seen: string;
}

interface ChatContextType {
  isConnected: boolean;
  isConnecting: boolean;
  socket: ISocket | null;

  messages: ChatMessage[];
  teamMembers: TeamMember[];

  currentRoom: string | null;

  currentUser: any;

  typingUsers: { userId: string; roomId: string }[];

  unreadCounts: Record<string, number>;

  lastMessages: Record<string, ChatMessage>;

  connect: () => void;
  disconnect: () => void;
  joinRoom: (roomId: string) => void;
  joinConversationRoom: (conversationId: string) => void;
  leaveRoom: (roomId: string) => void;
  sendMessage: (content: string, messageType?: string, metadata?: any) => void;
  sendDirectMessage: (recipientId: string, content: string, messageType?: string, metadata?: any) => void;
  forwardMessage: (targetRoomId: string, content: string, messageType?: string, metadata?: any) => Promise<boolean>;
  editMessage: (messageId: string, newContent: string) => void;
  deleteMessage: (messageId: string) => void;
  clearChat: (roomId: string) => Promise<boolean>;
  setTyping: (isTyping: boolean, roomId?: string) => void;
  markAsRead: (memberId: string) => void;
  markDmMessagesAsRead: (roomId: string) => void;

  isLoadingMessages: boolean;
  isLoadingMembers: boolean;
}

export const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState<ISocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [currentRoom, setCurrentRoom] = useState<string | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [typingUsers, setTypingUsers] = useState<{ userId: string; roomId: string }[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [lastMessages, setLastMessages] = useState<Record<string, ChatMessage>>({});
  const typingTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const teamMembersLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const connect = useCallback(() => {
    if (socket || isConnecting) return;

    setIsConnecting(true);

    const envSocketUrl = import.meta.env.VITE_WEBSOCKET_URL;
    const socketUrl = envSocketUrl || API_CONFIG.BASE_URL.replace('/api/v1', '');

    const newSocket = createSocket(socketUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      chatLog('[ChatContext] Socket.IO connected! ID:', newSocket.id);
      if (newSocket.id) void(0);
      setIsConnected(true);
      setIsConnecting(false);

      if (user?.id) {
        chatLog('[ChatContext] Authenticating with user ID:', user.id);
        newSocket.emit('authenticate', {
          userId: user.id,
          token: localStorage.getItem('auth_token') || undefined,
        });
      }
    });


    newSocket.on('authenticated', (data) => {
      chatLog('[ChatContext] Socket.IO authenticated:', data);

      if (user?.id) {
        chatLog('[ChatContext] Joining user room: user_' + user.id);
        newSocket.emit('join_room', { roomId: `user_${user.id}` });
      }

      if ((user as any)?.organization_id) {
        const orgRoom = `organization_${(user as any).organization_id}`;
        chatLog('[ChatContext] Joining organization room:', orgRoom);
        newSocket.emit('join_room', { roomId: orgRoom });
      }

    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      setIsConnecting(false);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      setIsConnected(true);

      if (user?.id) {
        newSocket.emit('authenticate', {
          userId: user.id,
          token: localStorage.getItem('auth_token') || undefined,
        });
      }
    });

    newSocket.on('reconnect_error', (error) => {
    });

    newSocket.on('reconnect_failed', () => {
      setIsConnected(false);
      setIsConnecting(false);
    });

    newSocket.on('message', (data) => {
      if (data.type === 'message' || data.type === 'direct_message') {
        const statusRank: Record<string, number> = { sent: 0, delivered: 1, read: 2 };
        setMessages(prev => {
          const existing = prev.find(m => m.id === data.data.id);
          if (existing) {
            const incomingRank = statusRank[data.data.status] ?? 0;
            const existingRank = statusRank[existing.status] ?? 0;
            if (incomingRank > existingRank) {
              return prev.map(m => m.id === data.data.id ? { ...m, status: data.data.status } : m);
            }
            return prev;
          }
          return [...prev, data.data];
        });

        if (data.type === 'direct_message' && data.data.room_id) {
          const otherId = getOtherUserId(data.data.room_id, user?.id || '');
          if (otherId) {
            setLastMessages(prev => ({ ...prev, [otherId]: data.data }));
            if (data.data.sender_id !== user?.id) {
              setUnreadCounts(prev => ({
                ...prev,
                [otherId]: (prev[otherId] || 0) + 1
              }));
            }
          }
        }

        if (data.type === 'message' && data.data.room_id && !data.data.room_id.startsWith('dm_')) {
          if (data.data.sender_id !== user?.id) {
            setUnreadCounts(prev => ({
              ...prev,
              [data.data.room_id]: (prev[data.data.room_id] || 0) + 1
            }));
          }
        }
      }
    });

    newSocket.on('chat_history', (data) => {
      setMessages(prev => {
        const filtered = prev.filter(msg => msg.room_id !== data.roomId);
        return [...filtered, ...data.messages];
      });

      if (data.roomId?.startsWith('dm_') && data.messages.length > 0) {
        const otherId = getOtherUserId(data.roomId, user?.id || '');
        if (otherId) {
          setLastMessages(prev => ({
            ...prev,
            [otherId]: data.messages[data.messages.length - 1]
          }));
        }
      }
    });

    newSocket.on('message_updated', (data) => {
      setMessages(prev => prev.map(msg =>
        msg.id === data.messageId
          ? { ...msg, content: data.newContent, updated_at: data.updatedAt }
          : msg
      ));
    });

    newSocket.on('message_deleted', (data) => {
      setMessages(prev => prev.filter(msg => msg.id !== data.messageId));
    });

    newSocket.on('messages_status_updated', (data) => {
      if (data.messageIds && data.status) {
        const idSet = new Set(data.messageIds);
        setMessages(prev => prev.map(msg =>
          idSet.has(msg.id) ? { ...msg, status: data.status } : msg
        ));
      }
    });


    newSocket.on('user_status', (data) => {
      setTeamMembers((prev) => {
        if (!prev || prev.length === 0) {
          return prev;
        }

        return prev.map((member) =>
          member.id === data.userId
            ? {
              ...member,
              is_online: data.is_online,
              last_seen: data.last_seen,
            }
            : member
        );
      });
    });

    newSocket.on('room_joined', (data) => {
      setCurrentRoom(data.roomId);
    });

    newSocket.on('room_left', (data) => {
      if (currentRoom === data.roomId) {
        setCurrentRoom(null);
      }
    });

    newSocket.on('user_typing', (data) => {
      const key = `${data.userId}_${data.roomId}`;

      if (typingTimeoutsRef.current[key]) {
        clearTimeout(typingTimeoutsRef.current[key]);
        delete typingTimeoutsRef.current[key];
      }

      if (data.isTyping) {
        setTypingUsers(prev => {
          if (prev.some(t => t.userId === data.userId && t.roomId === data.roomId)) return prev;
          return [...prev, { userId: data.userId, roomId: data.roomId }];
        });
        typingTimeoutsRef.current[key] = setTimeout(() => {
          setTypingUsers(prev => prev.filter(t => !(t.userId === data.userId && t.roomId === data.roomId)));
          delete typingTimeoutsRef.current[key];
        }, 3000);
      } else {
        setTypingUsers(prev => prev.filter(t => !(t.userId === data.userId && t.roomId === data.roomId)));
      }
    });

    newSocket.on('error', (data) => {
    });

    newSocket.on('conversation_updated', (data) => {
    });

    newSocket.on('conversations_updated', (data) => {
    });

    newSocket.on('messages_marked_read', (data) => {
    });

    setSocket(newSocket);
  }, [socket, isConnecting, user?.id, currentRoom]);

  const disconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }
  }, [socket]);

  const joinRoom = useCallback(async (roomId: string) => {
    chatLog('[ChatContext] Joining room:', roomId);
    setCurrentRoom(roomId);
    
    try {
      const { ChatService } = await import('@/lib/api/services/chatService');
      const response = await ChatService.getChatHistory(roomId, 50, 0);
      
      if (response.success && Array.isArray(response.data)) {
        chatLog('[ChatContext] Chat history loaded:', response.data.length, 'messages');
        
        setMessages(prev => {
          const filtered = prev.filter(msg => msg.room_id !== roomId);
          return [...filtered, ...response.data];
        });
      }
    } catch (error) {
      console.error('[ChatContext] Error loading chat history:', error);
    }
    
    if (socket && isConnected) {
      socket.emit('join_room', { roomId });
    }
  }, [socket, isConnected]);

  const joinConversationRoom = useCallback((conversationId: string) => {
    if (socket && isConnected) {
      const roomId = `conversation_${conversationId}`;
      chatLog('[ChatContext] Joining room:', roomId);
      socket.emit('join_room', { roomId });
    } else {
      console.warn('[ChatContext] Cannot join room - socket:', !!socket, 'isConnected:', isConnected);
    }
  }, [socket, isConnected]);

  const leaveRoom = useCallback((roomId: string) => {
    if (socket && isConnected) {
      socket.emit('leave_room', {
        roomId
      });
    }
  }, [socket, isConnected]);

  const sendMessage = useCallback(async (content: string, messageType = 'text', metadata?: any) => {
    if (!currentRoom) {
      console.error('[ChatContext] Cannot send message - no room selected');
      return false;
    }

    if (!user?.id) {
      console.error('[ChatContext] Cannot send message - user not authenticated');
      return false;
    }

    try {
      chatLog('[ChatContext] Sending message via REST API to room:', currentRoom);
      
      const { ChatService } = await import('@/lib/api/services/chatService');
      
      const response = await ChatService.sendMessage({
        room_id: currentRoom,
        content,
        message_type: messageType as any,
        metadata
      });

      if (response.success && response.data) {
        chatLog('[ChatContext] Message sent successfully:', response.data);
        
        const messageData = response.data;
        setMessages(prev => {
          if (prev.some(m => m.id === messageData.id)) return prev;
          return [...prev, messageData];
        });
        return true;
      } else {
        console.error('[ChatContext] Failed to send message:', response);
        return false;
      }
    } catch (error) {
      console.error('[ChatContext] Error sending message:', error);
      return false;
    }
  }, [currentRoom, user?.id]);

  const sendDirectMessage = useCallback(async (recipientId: string, content: string, messageType = 'text', metadata?: any) => {
    if (!user?.id) {
      console.error('[ChatContext] Cannot send message - user not authenticated');
      return false;
    }

    try {
      chatLog('[ChatContext] Sending direct message via REST API to:', recipientId);
      
      const { ChatService } = await import('@/lib/api/services/chatService');
      
      const response = await ChatService.sendDirectMessage(user.id, {
        recipientId,
        content,
        message_type: messageType as any,
        metadata
      });

      if (response.success && response.data) {
        chatLog('[ChatContext] Direct message sent successfully:', response.data);

        const messageData = response.data as any;
        setMessages(prev => {
          const existing = prev.find(m => m.id === messageData.id);
          if (existing) {
            return prev.map(m => m.id === messageData.id ? { ...m, ...messageData } : m);
          }
          return [...prev, messageData];
        });

        setLastMessages(prev => ({
          ...prev,
          [recipientId]: messageData
        }));
        return true;
      } else {
        console.error('[ChatContext] Failed to send direct message:', response);
        return false;
      }
    } catch (error) {
      console.error('[ChatContext] Error sending direct message:', error);
      return false;
    }
  }, [user?.id]);

  const forwardMessage = useCallback(async (
    targetRoomId: string,
    content: string,
    messageType = 'text',
    metadata?: any
  ): Promise<boolean> => {
    if (!user?.id) {
      console.error('[ChatContext] Cannot forward message - user not authenticated');
      return false;
    }
    if (!targetRoomId) {
      console.error('[ChatContext] Cannot forward message - targetRoomId missing');
      return false;
    }
    try {
      const { ChatService } = await import('@/lib/api/services/chatService');
      const response = await ChatService.sendMessage({
        room_id: targetRoomId,
        content,
        message_type: messageType as any,
        metadata
      });
      if (response.success && response.data) {
        const messageData = response.data as any;
        setMessages(prev => {
          if (prev.some(m => m.id === messageData.id)) return prev;
          return [...prev, messageData];
        });
        if (targetRoomId.startsWith('dm_')) {
          const otherId = getOtherUserId(targetRoomId, user.id);
          if (otherId) {
            setLastMessages(prev => ({ ...prev, [otherId]: messageData }));
          }
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('[ChatContext] Error forwarding message:', err);
      return false;
    }
  }, [user?.id]);

  const setTyping = useCallback((isTyping: boolean, roomId?: string) => {
    const targetRoom = roomId || currentRoom;
    if (socket && isConnected && targetRoom) {
      socket.emit('typing', {
        roomId: targetRoom,
        isTyping
      });
    }
  }, [socket, isConnected, currentRoom]);

  const markAsRead = useCallback((memberId: string) => {
    setUnreadCounts(prev => {
      if (prev[memberId]) {
        const { [memberId]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const editMessage = useCallback((messageId: string, newContent: string) => {
    if (socket && isConnected) {
      socket.emit('edit_message', {
        messageId,
        newContent
      });
    }
  }, [socket, isConnected]);

  const deleteMessage = useCallback((messageId: string) => {
    if (socket && isConnected) {
      socket.emit('delete_message', {
        messageId
      });
    }
  }, [socket, isConnected]);

  const markDmMessagesAsRead = useCallback((roomId: string) => {
    if (socket && isConnected && roomId) {
      socket.emit('mark_messages_read', { roomId });
    }
  }, [socket, isConnected]);

  const clearChat = useCallback(async (roomId: string): Promise<boolean> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return false;

      const res = await fetch(`${API_CONFIG.BASE_URL}/chat/clear/${encodeURIComponent(roomId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) return false;

      setMessages(prev => prev.filter(msg => msg.room_id !== roomId));

      if (roomId.startsWith('dm_')) {
        const parts = roomId.split('_');
        const otherUserId = parts[1] === user?.id ? parts[2] : parts[1];
        if (otherUserId) {
          setLastMessages(prev => {
            const { [otherUserId]: _, ...rest } = prev;
            return rest;
          });
        }
      }

      return true;
    } catch (error) {
      console.error('[ChatContext] Error clearing chat:', error);
      return false;
    }
  }, [user?.id]);

  const loadTeamMembers = useCallback(async () => {
    chatLog('[ChatContext] Loading team members via REST API...');
    setIsLoadingMembers(true);
    
    try {
      const { TeamService } = await import('@/lib/api/services/teamService');
      
      const response = await TeamService.getTeamMembers({ limit: 100 });
      
      if (response.success && Array.isArray(response.data)) {
        chatLog('[ChatContext] Team members loaded successfully:', response.data.length);
        
        const members = response.data.map((member: any) => ({
          id: member.id,
          email: member.email,
          full_name: member.name || member.full_name || member.email,
          avatar_url: member.avatar_url || null,
          role: member.role || 'USER',
          is_online: member.is_online || false,
          last_seen: member.last_seen || new Date().toISOString()
        }));
        
        setTeamMembers(members);
      } else {
        console.warn('[ChatContext] Failed to load team members:', response);
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('[ChatContext] Error loading team members:', error);
      setTeamMembers([]);
    } finally {
      setIsLoadingMembers(false);
      
      if (teamMembersLoadTimeoutRef.current) {
        clearTimeout(teamMembersLoadTimeoutRef.current);
        teamMembersLoadTimeoutRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      chatLog('[ChatContext] Auto-loading team members via REST API...');
      loadTeamMembers();
    }
  }, [user?.id, loadTeamMembers]);

  useEffect(() => {
    if (user?.id && !socket && !isConnecting) {
      connect();
    }
  }, [user?.id, socket, isConnecting, connect]);


  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      if (teamMembersLoadTimeoutRef.current) {
        clearTimeout(teamMembersLoadTimeoutRef.current);
      }
    };
  }, [socket]);

  const value: ChatContextType = {
    isConnected,
    isConnecting,
    socket,
    messages,
    teamMembers,
    currentRoom,
    currentUser: user,
    typingUsers,
    unreadCounts,
    lastMessages,
    connect,
    disconnect,
    joinRoom,
    joinConversationRoom,
    leaveRoom,
    sendMessage,
    sendDirectMessage,
    forwardMessage,
    editMessage,
    deleteMessage,
    clearChat,
    setTyping,
    markAsRead,
    markDmMessagesAsRead,
    isLoadingMessages,
    isLoadingMembers
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  return context;
}
